import * as React from "react";
import classes from "./AddSystem.module.css";
import FormControlLabel from "@mui/material/FormControlLabel";
import SnackBar from "components/SnackBar/SnackBar";
import SystemCredentials from "../SystemCredentials/SystemCredentials";
import { useSelector, useDispatch } from "react-redux";
import {
  getSystemDetailsByEmployeeId,
  deleteSystemDetails,
} from "services/centerPointUserServices";
import moment from "moment";
import { addNonHRMSEmployeeActions } from "Store/addNonHRMSEmployee";
import { Dialog, Tooltip, Switch } from "@mui/material";
import DeleteConfirmDialog from "../DeleteConfirmDialog/DeleteConfirmDialog";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import Divider from "@mui/material/Divider";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";
import { Transition } from "components/Common/Header/Header";
import { useAuth } from "context/AuthContext";

const AddSystem = () => {
  const dispatch = useDispatch();
  const { hasPermission } = useAuth();
  const canDeleteLogonId = hasPermission("CP_Group");
  const details = useSelector((state) => state.addNonHRMSEmployee);
  const [initialRows, setInitialRows] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [addOrEdit, setAddOrEdit] = React.useState("");
  const [isActive, setIsActive] = React.useState("Active");
  const [snack, setSnack] = React.useState({
    open: false,
    message: "",
    severity: "",
  });
  const [dataToBeEdited, setDataToBeEdited] = React.useState({});
  const [gridPageSize, setGridPageSize] = React.useState(10);
  const [rowData, setRowData] = React.useState([]);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [cancelRow, setCancelRow] = React.useState(false);

  const handleToggle = (event) => {
    setIsActive(event.target.checked ? "Active" : "InActive");
  };

  const onRowSelectChange = (event) => {
    try {
      if (event.checked) {
        setSelectedRows(rowData);
      } else if (event.originalEvent.target.checked === false) {
        setSelectedRows([]);
      } else {
        setSelectedRows(event.value);
      }
    } catch (error) {
      setSnack({
        open: true,
        message: "Error occurred while changing row selection.",
        severity: "error",
      });
    }
  };

  const calculatePageSize = () => {
    const height = window.innerHeight;
    if (height > 1200) return 100;
    if (height > 900) return 50;
    if (height > 600) return 25;
    return 10;
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const CustomIconButton = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    color: theme.palette.primary.main,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
      color: theme.palette.primary.dark,
    },
    "& .MuiTypography-root": {
      marginRight: theme.spacing(1),
    },
  }));

  const IconButtonWithLabel = ({ onClick }) => (
    <CustomIconButton onClick={onClick}>
      <BorderColorIcon sx={{ fontSize: 30 }} />
    </CustomIconButton>
  );

  IconButtonWithLabel.propTypes = {
    onClick: PropTypes.func.isRequired,
  };

  const DeleteIconButton = ({ onClick, disabled }) => (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        marginLeft: 1,
        color: disabled ? "action.disabled" : "error.main",
        cursor: disabled ? "not-allowed" : "pointer",
        "&:hover": {
          textDecoration: disabled ? "none" : "underline",
          color: disabled ? "action.disabled" : "error.dark",
        },
      }}
    >
      <DeleteOutlineOutlinedIcon sx={{ fontSize: 30 }} />
    </Box>
  );

  DeleteIconButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
  };

  const renderWithTooltip = (field) => (params) => {
    const value = params?.[field];
    if (!value) return "";
    return (
      <Tooltip title={value} arrow placement="top">
        <span>{value}</span>
      </Tooltip>
    );
  };

  const columns = [
    {
      field: "type",
      header: "Type",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Type",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("type"),
    },
    {
      field: "systemName",
      header: "System",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by System Name",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("systemName"),
    },
    {
      field: "subSystem",
      header: "Sub System",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Sub System",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("subSystem"),
    },
    {
      field: "logonId",
      header: "Logon ID",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Logon Id",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("logonId"),
    },
    {
      field: "effectiveDate",
      header: "Effective Date",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Effective Date",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("effectiveDate"),
    },
    {
      field: "expireDate",
      header: "Expire Date",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Expire Date",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("expireDate"),
    },
    {
      field: "employeeSystemStatus",
      header: "Status",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Status",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("employeeSystemStatus"),
    },
    {
      field: "selection",
      header: "",
      sortable: false,
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <Tooltip title="Edit" arrow placement="top">
          <span>
            <IconButtonWithLabel
              onClick={() => {
                setAddOrEdit("Edit");
                setDataToBeEdited(params);
              }}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      field: "delete",
      header: "",
      sortable: false,
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <Tooltip
          title={
            canDeleteLogonId ? "Delete" : "You do not have permission to delete"
          }
          arrow
          placement="top"
        >
          <span>
            <DeleteIconButton
              disabled={!canDeleteLogonId}
              onClick={() => setDeleteRow(params)}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  React.useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (details?.hrEmployeeId) {
        try {
          await fetchData();
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to fetch data:", error);
          }
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [details?.hrEmployeeId]);

  const filterProps = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    systemName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    subSystem: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    logonId: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    effectiveDate: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    expireDate: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    employeeSystemStatus: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
  };

  const fetchData = async () => {
    dispatch(addNonHRMSEmployeeActions.updateSystemDetailsLoader(true));
    try {
      const response = await getSystemDetailsByEmployeeId(
        details?.hrEmployeeId,
      );
      if (response?.data?.message === "Success") {
        dispatch(
          addNonHRMSEmployeeActions.updateEmployeeId(
            response?.data?.data[0]?.CP_EmployeeId,
          ),
        );
        const data = response?.data?.data?.map((rowItem, index) => ({
          id: index,
          OracleId: rowItem?.OracleId,
          CP_EmployeeId: rowItem?.CP_EmployeeId,
          type: rowItem?.Type,
          Location: rowItem?.Location,
          SubSystemId: rowItem?.SubSystemId,
          subSystem: rowItem["Sub System"],
          SystemId: rowItem?.SystemId,
          systemName: rowItem["System Name"],
          logonId: rowItem["Logon Id"],
          effectiveDate: rowItem["Effective Date"]
            ? moment.utc(rowItem["Effective Date"]).format("YYYY-MM-DD")
            : "",
          expireDate: rowItem["Expire Date"]
            ? moment.utc(rowItem["Expire Date"]).format("YYYY-MM-DD")
            : "",
          employeeSystemStatus: rowItem?.EmployeeSystemStatus
            ? "Active"
            : "InActive",
          SystemTypeId: rowItem?.CP_SystemTypeId,
          GeoLocGLCdId: rowItem?.GeoLocGLCdId,
          EmployeeSystemId: rowItem?.CP_EmployeeSystemId,
        }));
        setRowData([]);
        const filteredData = data?.filter(
          (item) => item?.employeeSystemStatus === isActive,
        );
        const updatePageSize = () => {
          setGridPageSize(calculatePageSize());
        };
        updatePageSize();
        window.addEventListener("resize", updatePageSize);
        setInitialRows(data);
        setRows(filteredData);
        setRowData(filteredData);
      } else {
        if (response?.data?.statusCode === 404) {
          setSnack({
            open: true,
            message: "No system details record found",
            severity: "info",
          });
        } else
          setSnack({
            open: true,
            message: "Could not fetch the details of the employee",
            severity: "error",
          });
      }

      dispatch(addNonHRMSEmployeeActions.updateSystemDetailsLoader(false));
    } catch (error) {
      errorHandler(true, "Something went wrong !", "error");
    }
  };

  React.useEffect(() => {
    const data = initialRows?.filter(
      (rowItem) => rowItem?.employeeSystemStatus === isActive,
    );
    setRows(data);
  }, [isActive]);

  const errorHandler = (open, message, severity) => {
    setSnack({
      open: open,
      message: message,
      severity: severity,
    });
  };

  const handleCancelAddOrEdit = () => {
    setAddOrEdit("");
  };

  const handleCancelDelete = () => {
    if (cancelRow) return;
    setDeleteRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow?.EmployeeSystemId) {
      errorHandler(true, "Unable to delete: record missing.", "error");
      setDeleteRow(null);
      return;
    }
    setCancelRow(true);
    try {
      const response = await deleteSystemDetails({
        EmployeeSystemId: deleteRow.EmployeeSystemId,
        LoggedOnUserName: localStorage.getItem("User"),
      });
      const result = response?.data?.data;
      const isSuccess =
        response?.data?.message === "Success" &&
        !(result?.IsError ?? result?.isError);

      if (isSuccess) {
        errorHandler(true, "Logon ID deleted successfully.", "success");
        setDeleteRow(null);
        await fetchData();
      } else {
        errorHandler(
          true,
          result?.ErrorMessage ||
            result?.errorMessage ||
            "This record could not be deleted. It may have already been removed.",
          "error",
        );
        setDeleteRow(null);
      }
    } catch (error) {
      errorHandler(
        true,
        "Something went wrong while deleting. Please try again.",
        "error",
      );
      setDeleteRow(null);
    }
    setCancelRow(false);
  };

  const handleAddSystem = () => {
    setAddOrEdit("Add");
  };

  return (
    <>
      {" "}
      <div className={classes.container}>
        <div className={classes.buttonContainer}>
          <div>
            <p className={classes.statusText}> Status :</p>
          </div>
          <div className={classes.switch}>
            <FormControlLabel
              control={
                <Switch
                  sx={{ color: "#3299FF" }}
                  checked={isActive === "Active"}
                  onChange={handleToggle}
                />
              }
              label={isActive}
              sx={{
                fontFamily: "Urbanist",
                color: isActive === "InActive" ? "#3299FF" : "inherit",
              }}
              value={isActive ? "Active" : "InActive"}
            />
            {!addOrEdit && (
              <CustomButton
                sx={{ backgroundColor: "#3299FF" }}
                variant="contained"
                buttonClass={classes.buttonClass}
                onClick={handleAddSystem}
              >
                Add System{" "}
              </CustomButton>
            )}
          </div>
        </div>
        <div className={classes.primeReactGridContainer}>
          <PrimeReactDataGrid
            columns={columns}
            dataKey="id"
            filterprops={filterProps}
            globalFilterFields={[
              "type",
              "systemName",
              "subSystem",
              "logonId",
              "effectiveDate",
              "expireDate",
              "employeeSystemStatus",
            ]}
            removableSort
            resizableColumns
            showGridlines
            paginator
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} record(s)"
            columnResizeMode="expand"
            value={rows}
            selectionMode="single"
            dragSelection
            metaKeySelection={false}
            rows={gridPageSize}
            onSelectionChange={onRowSelectChange}
            selection={selectedRows}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </div>
        <div>
          <Divider
            sx={{
              flexGrow: 1,
              ml: 3,
              borderBottomWidth: 2,
            }}
          ></Divider>
        </div>

        <Dialog
          open={addOrEdit !== ""}
          onClose={handleCancelAddOrEdit}
          maxWidth="lg"
          fullWidth
          TransitionComponent={Transition}
        >
          <SystemCredentials
            addOrEdit={addOrEdit}
            handleCancelAddOrEdit={handleCancelAddOrEdit}
            employeeId={details?.CP_EmployeeId}
            dataToBeEdited={dataToBeEdited}
            errorHandler={errorHandler}
            fetchData={fetchData}
          />
        </Dialog>
        <DeleteConfirmDialog
          open={!!deleteRow}
          logonId={deleteRow?.logonId}
          loading={cancelRow}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </>
  );
};

export default AddSystem;
